import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { AnimatedElement } from "./AnimatedElement";
import { Tweens } from "../Components/Effects/Tweens";
import { ProtonElement } from "../Components/Elements/Graphics/ProtonElement";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";

@engineComponent(ComponentTypes.DisplayObject)
export class AnimatedSymbol extends AnimatedElement {
    private _status: string = "animate";
    private _idleTimer: FrameTimerEventEmitter;
    private _winYOffset: number = 12;

    constructor() {
        super();

        this._idleTimer = CreateFrameTimer("introDelay");
        this._idleTimer.on(FrameTimerEvents.Complete, this.idle.bind(this));
    }

    public createByNode(node: Element) {
        super.createByNode(node);
    }

    public updateByNode(node: Element) {
        super.updateByNode(node);

        if (node.hasAttribute("Tints")) {
            let tints: Array<string> = node.getAttribute("Tints").split(";");
            if (tints != null) {
                for (let i: number = 0; i < tints.length; i++) {
                    if (this.childs["Object" + i] != null) {
                        this.childs["Object" + i].tint = parseInt(tints[i], 16);
                    }
                }
            }
        }

        if (node.hasAttribute("WinYOffset")) {
            this._winYOffset = parseInt(node.getAttribute("WinYOffset"));
        }

        if (node.hasAttribute("GlobalTint")) {
            for (let key in this.childs) {
                this.childs[key].tint = parseInt(node.getAttribute("GlobalTint"), 16);
            }
        }

        if (node.hasAttribute("Method")) {
            switch (node.getAttribute("Method")) {
                case "nowin":
                    if (this._status !== "animate") {
                        this.specialParticles(0.1);
                        this.animate();
                        this._status = "animate";
                    }
                    break;
                case "startRotation":
                    this.specialParticles();
                    this.startRotation();
                    this._status = "startRotation";
                    break;
                case "rotation":
                    this.specialParticles();
                    this.stopRotation();
                    this._status = "stopRotation";
                    break;
                case "animate":
                    this.specialParticles();
                    if (this._status !== "animate" && this._status !== "stopRotation") {
                        this.alpha = 1;
                        this.animate();
                        this._status = "animate";
                    }
                    break;
                case "win":
                    this.specialParticles();
                    if (this._status !== "win") {
                        this.alpha = 1;
                        this.animateWin();
                        this._status = "win";
                    }
                    break;
                case "cycle":
                    this.specialParticles();
                    if (this._status !== "win") {
                        this.alpha = 1;
                        this.animateWin();
                        this._status = "win";
                    }
                    break;
                case "reveal":
                    this.specialParticles();

                    let values: Array<number> = [0];
                    let durations: Array<number> = [0];
                    let delays: Array<number> = [0];
                    let ease: Array<string> = ["linear"];

                    if (node.hasAttribute("ObjectsTweenValues")) {
                        values = ArrayUtils.numberArray(node.getAttribute("ObjectsTweenValues"));
                    }

                    if (node.hasAttribute("ObjectsTweenDurations")) {
                        durations = ArrayUtils.numberArray(node.getAttribute("ObjectsTweenDurations"));
                    }

                    if (node.hasAttribute("ObjectsTweenDelay")) {
                        delays = ArrayUtils.numberArray(node.getAttribute("ObjectsTweenDelay"));
                    }

                    if (node.hasAttribute("ObjectsTweenEase")) {
                        ease = ArrayUtils.stringArray(node.getAttribute("ObjectsTweenEase"));
                    }

                    this.reveal(values, durations, delays, ease);
                    this._status = node.hasAttribute("Status") ? node.getAttribute("Status") : "reveal";
                    break;
            }
        }
    }

    public start(): void {
        super.start();
        let randomIdle: number = parseInt(this.getRandom(120, 240).toString());
        this._idleTimer.start(randomIdle);
    }

    public stop(): void {
        super.stop();
    }

    public clone(): AnimatedSymbol {
        const element: AnimatedSymbol = new AnimatedSymbol();
        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.childs = {};
        element._lastY = this.y;
        element.removeChildren();
        for (let key in this.childs) {
            element.childs[key] = this.childs[key].clone();
            element.addChild(element.childs[key]);
            if (element.childs[key] instanceof ProtonElement) {
                element.childs[key].start();
            }
            if (element.childs[key] instanceof AnimationElement) {
                element.childs[key].playMe();
            }
        }
        let container: PIXI.Container = new PIXI.Container();
        if (element.children != null) {
            while (element.children.length !== 0) {
                container.addChild(element.children[0]);
            }
        }
        element.addChild(container);
        element._winYOffset = this._winYOffset;
        element._positionOffset = this._positionOffset;
        element._rotationOffset = this._rotationOffset;
        element._scaleOffset = this._scaleOffset;
        element._rotationDuration = this._rotationDuration;
        element._scaleDuration = this._scaleDuration;
        element._timerRange = this._timerRange;
        element._positionRangeDuration = this._positionRangeDuration;
        element.start();
        element.markAsCloned();
        return element;
    }

    public startRotation(): void {
        Tweens.removeTweens(this, "scale");
        Tweens.scaleTween(this, {
            tweenValues: [new PIXI.Point(this.scale.x, this.scale.y), new PIXI.Point(0.8, 1.2)],
            tweenDuration: 15,
            tweenEase: "Back.backOut",
        });

        let i: number = 0;
        while (i >= 0) {
            if (this.childs["ObjectWin" + i] != null) {
                Tweens.removeTweens(this.childs["ObjectWin" + i], "alpha");
                Tweens.alphaTween(this.childs["ObjectWin" + i], {
                    alphas: [this.childs["ObjectWin" + i].alpha, 0],
                    tweenDuration: 5,
                });
                i++;
            } else {
                i = -1;
            }
        }
    }

    public stopRotation(): void {
        Tweens.removeTweens(this, "scale");
        Tweens.scaleTween(this, {
            tweenValues: [
                new PIXI.Point(0.7, 1.2),
                new PIXI.Point(1, 0.8),
                new PIXI.Point(0.8, 1.2),
                new PIXI.Point(1, 1),
            ],
            tweenDuration: 30,
            tweenEase: "Back.backOut",
        });
    }

    public animate(): void {
        super.animate();

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.x, this.x],
            tweenY: [this.y, this._lastY],
            tweenDuration: 30,
        });

        let i: number = 0;
        while (i >= 0) {
            if (this.childs["ObjectWin" + i] != null) {
                Tweens.removeTweens(this.childs["ObjectWin" + i], "alpha");
                Tweens.alphaTween(this.childs["ObjectWin" + i], {
                    alphas: [this.childs["ObjectWin" + i].alpha, 0],
                    tweenDuration: 10,
                });
                i++;
            } else {
                i = -1;
            }
        }

        //only bonus symbol
        if (this.childs["Light0"] && this.childs["Light1"]) {
            this.pivot.x = this.childs["Light0"].pivot.x;
            this.pivot.y = this.childs["Light0"].pivot.y;

            if (!Tweens.getRegisteredTweens(this.childs["Light0"].uid)) {
                Tweens.rotationTween(this.childs["Light0"], {
                    degrees: [15, 0, 15],
                    tweenDuration: 600,
                    tweenLoop: true,
                    tweenEase: "sineInOut",
                });

                Tweens.rotationTween(this.childs["Light1"], {
                    degrees: [0, 15, 0],
                    tweenDuration: 600,
                    tweenLoop: true,
                    tweenEase: "sineInOut",
                });
            }
        }
    }

    public animateWin(): void {
        Tweens.removeTweens(this, "scale");
        Tweens.scaleTween(this, {
            tweenValues: [new PIXI.Point(this.scale.x, this.scale.y), new PIXI.Point(1, 1)],
            tweenDuration: 5,
            callbackFunction: (): void => {
                Tweens.scaleTween(this, {
                    tweenValues: [new PIXI.Point(1, 1), new PIXI.Point(0.95, 1.05), new PIXI.Point(1, 1)],
                    tweenEase: "Back.backOut",
                    tweenDuration: 30,
                    tweenLoop: true,
                });
            },
        });

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.x, this.x, this.x, this.x],
            tweenY: [this._lastY, this._lastY - this._winYOffset, this._lastY + this._winYOffset, this._lastY],
            tweenEase: "Back.backIn",
            tweenDuration: 60,
            tweenLoop: true,
        });

        let i: number = 0;
        while (i >= 0) {
            if (this.childs["ObjectWin" + i] != null) {
                Tweens.removeTweens(this.childs["ObjectWin" + i], "alpha");
                Tweens.alphaTween(this.childs["ObjectWin" + i], {
                    alphas: [this.childs["ObjectWin" + i].alpha, 0.5, 0],
                    tweenDuration: 60,
                    tweenLoop: true,
                });
                i++;
            } else {
                i = -1;
            }
        }
    }

    public reveal(values: Array<number>, durations: Array<number>, delays: Array<number>, ease: Array<string>): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.x, this.x],
            tweenY: [this.y, this._lastY],
            tweenDuration: 30,
        });

        let i: number = 0;
        while (i >= 0) {
            if (this.childs["Object" + i] != null) {
                let valuesArray = [...values];

                let baseScale = this.childs["Object" + i].scale.x;
                if (baseScale == 2) {
                    for (let j = 0; j < valuesArray.length; j++) {
                        valuesArray[j] = valuesArray[j] * baseScale;
                    }
                }

                Tweens.removeTweens(this.childs["Object" + i], "scale");
                Tweens.customTween(this.childs["Object" + i], {
                    tween: "scale.x",
                    tweenValues: [...valuesArray],
                    tweenDuration: durations,
                    tweenDelay: [delays[i]],
                    tweenEase: ease,
                });
                Tweens.customTween(this.childs["Object" + i], {
                    tween: "scale.y",
                    tweenValues: [...valuesArray],
                    tweenDuration: durations,
                    tweenDelay: [delays[i]],
                    tweenEase: ease,
                });
                i++;
            } else {
                i = -1;
            }
        }
    }

    public move(): void {
        super.move();
    }

    public specialParticles(alpha: number = 1): void {
        let i: number = 0;
        while (i >= 0) {
            if (this.childs["Object" + i] != null) {
                if (this.childs["Object" + i] instanceof ProtonElement) {
                    this.childs["Object" + i].alpha = alpha;
                }
                i++;
            } else {
                i = -1;
            }
        }
    }

    private idle(): void {
        let i: number = 0;
        while (i >= 0) {
            if (this.childs["ObjectIdle" + i] != null) {
                if (this.childs["ObjectIdle" + i] instanceof AnimationElement) {
                    this.childs["ObjectIdle" + i].playMe();
                }
                i++;
            } else {
                i = -1;
            }
        }

        let randomIdle: number = parseInt(this.getRandom(120, 240).toString());
        this._idleTimer.start(randomIdle);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/AnimatedSymbol.ts
 **/